import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import useSettings from "@/lib/useSettings";
import { useState } from "react";
import { Menu, X, Lock, LogOut, Facebook, Shield, User as UserIcon } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { to: "/o-klubie", label: "O Klubie" },
    { to: "/galeria", label: "Galeria" },
    { to: "/kontakt", label: "Kontakt" },
  ];

  // Optional public tabs (visibility controlled from Settings)
  const partnersVis = settings.partners_visibility || "hidden";
  const inMemoryVis = settings.in_memory_visibility || "hidden";
  if (partnersVis === "all" || (partnersVis === "members" && user)) {
    navItems.push({ to: "/znajomi", label: "Nasi znajomi" });
  }
  if (inMemoryVis === "all" || (inMemoryVis === "members" && user)) {
    navItems.push({ to: "/in-memory", label: "In Memory" });
  }

  const memberItems = user
    ? [
        { to: "/strefa", label: "Strefa" },
        { to: "/wydarzenia", label: "Wydarzenia" },
        { to: "/historia", label: "Historia" },
        { to: "/regulamin", label: "Regulamin" },
        { to: "/forum", label: "Forum" },
        { to: "/czlonkowie", label: "Członkowie" },
        { to: "/formacja", label: "Szyk jazdy" },
      ]
    : [];

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  // Proportional scaling: hamburger only on real mobile (<640px)
  // Desktop/tablet keep full bar but font/gap shrink with viewport
  const linkStyle = {
    fontSize: "clamp(0.62rem, 0.9vw, 0.85rem)",
    letterSpacing: "clamp(0.04em, 0.1vw, 0.16em)",
    whiteSpace: "nowrap",
  };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{ background: "rgba(12,10,9,0.92)", borderBottom: "1px solid var(--sr-border)" }}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8">
        <div className="flex items-center justify-between" style={{ height: "clamp(48px, 5vw, 64px)" }}>
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_polski-hub-2/artifacts/quxt0b85_logo.jpg"
              alt="Scythe Riders"
              style={{
                width: "clamp(28px, 3vw, 40px)",
                height: "clamp(28px, 3vw, 40px)",
                objectFit: "contain",
                mixBlendMode: "screen",
                filter: "drop-shadow(0 0 6px rgba(255,106,26,0.4))",
              }}
            />
            <span
              className="font-display hidden xl:inline"
              style={{ color: "var(--sr-cream)", fontSize: "clamp(0.8rem, 1vw, 1rem)" }}
            >
              <span style={{ color: "var(--sr-fire)" }}>SCYTHE</span> RIDERS
            </span>
          </Link>

          {/* Desktop / Tablet — pokazane od 640px (sm:) */}
          <nav
            className="hidden sm:flex items-center font-nav uppercase"
            style={{ gap: "clamp(0.45rem, 1.3vw, 1.8rem)" }}
          >
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                style={linkStyle}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `transition-colors duration-200 ${
                    isActive ? "text-[var(--sr-fire)] border-b border-[var(--sr-fire)] pb-0.5" : "text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            {memberItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                style={linkStyle}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `transition-colors duration-200 ${
                    isActive ? "text-[var(--sr-fire)] border-b border-[var(--sr-fire)] pb-0.5" : "text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <NavLink
                to="/admin"
                style={linkStyle}
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-1 ${
                    isActive ? "text-[var(--sr-fire)] border-b border-[var(--sr-fire)] pb-0.5" : "text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)]"
                  }`
                }
              >
                <Shield size={12} /> Admin
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/profil"
                style={linkStyle}
                data-testid="nav-profil"
                className={({ isActive }) =>
                  `flex items-center gap-1 ${
                    isActive ? "text-[var(--sr-fire)] border-b border-[var(--sr-fire)] pb-0.5" : "text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)]"
                  }`
                }
                title="Mój profil"
              >
                <UserIcon size={15} /> <span className="hidden md:inline">Profil</span>
              </NavLink>
            )}
            {!user ? (
              <NavLink
                to="/logowanie"
                style={linkStyle}
                data-testid="nav-logowanie"
                className={({ isActive }) =>
                  `flex items-center gap-1 ${
                    isActive ? "text-[var(--sr-fire)]" : "text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)]"
                  }`
                }
              >
                Login <Lock size={11} />
              </NavLink>
            ) : (
              <button
                onClick={handleLogout}
                style={linkStyle}
                data-testid="nav-logout-btn"
                className="flex items-center gap-1 text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)] uppercase font-nav"
              >
                Wyloguj <LogOut size={11} />
              </button>
            )}
            {settings.fb_url ? (
              <a
                href={settings.fb_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--sr-cream-soft)] hover:text-[var(--sr-fire)] cursor-pointer"
                aria-label="Facebook"
                data-testid="nav-facebook"
              >
                <Facebook size={14} />
              </a>
            ) : null}
          </nav>

          {/* Mobile only (< 640px) */}
          <button
            className="sm:hidden text-[var(--sr-cream)]"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="sm:hidden pb-4 flex flex-col gap-2 font-nav text-sm" data-testid="mobile-menu">
            {[...navItems, ...memberItems].map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="uppercase text-[var(--sr-cream-soft)] py-2 border-b border-[var(--sr-border)]"
              >
                {it.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="uppercase text-[var(--sr-cream-soft)] py-2 border-b border-[var(--sr-border)]">
                Admin
              </NavLink>
            )}
            {user && (
              <NavLink to="/profil" onClick={() => setOpen(false)} className="uppercase text-[var(--sr-cream-soft)] py-2 border-b border-[var(--sr-border)]">
                Mój profil
              </NavLink>
            )}
            {!user ? (
              <NavLink to="/logowanie" onClick={() => setOpen(false)} className="uppercase text-[var(--sr-fire)] py-2">
                Logowanie
              </NavLink>
            ) : (
              <button onClick={handleLogout} className="uppercase text-left text-[var(--sr-fire)] py-2">
                Wyloguj
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
